import { Box, Paper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RefreshButton from "../../components/button/RefreshButton";
import { MainContainer } from "../../components/container/MainContainer";
import { PageTitleContainer } from "../../components/container/PageTitleContainer";
import ResizableBox from "../../components/container/ResizableBox";
import { TableHeaderContainer } from "../../components/container/TableHeaderContainer";
import { TableTitleContainer } from "../../components/container/TableTitleContainer";
import ContextMenu from "../../components/context-menu/ContextMenu";
import ExportAsXLSXButton from "../../components/export/ExportAsXLSXButton";
import AgGrid from "../../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, rightAlignColDef } from "../../components/grid/columnDefinitions/columnDefinitions";
import SearchBox from "../../components/input/SearchBox";
import { useContextProvider } from "../../components/page-context/ContextProvider";
import { Project } from "../../db_access/project";
import Footprint from "../../environment-page-components/Footprint";
import SankeyDiagram from "../../environment-page-components/SankeyDiagram";
import SystemBoundary from "../../environment-page-components/SystemBoundary";
// Cached hooks
import { TableHeaderToolsContainer } from "../../components/container/TableHeaderToolsContainer";
import { useInvalidateProjectCache, useProjectProcessData, useProjectsArchive } from "../../utilities/useProjectQueries";

const ProjectArchive = () => {
  const { t } = useTranslation();
  const { refresh, setRefresh, selectedLanguage } = useContextProvider();
  const gridRef = useRef(null);
  const gridHeaderContainerRef = useRef(null);
  const [selectedProjects, setSelectedProjects] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [menuVisible, setMenuVisible] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // CACHED: Fetch archived projects
  const { data: projects = [], refetch: refetchArchive } = useProjectsArchive();
  const { invalidateArchive } = useInvalidateProjectCache();

  // Selected project ID for process data
  const selectedProjectId = selectedProjects.length === 1 ? selectedProjects[0].id : null;

  // CACHED: Fetch process data for selected archive project
  const { data: processData = [] } = useProjectProcessData(selectedProjectId);

  // Manual refresh trigger
  const lastRefreshRef = useRef(refresh);
  useEffect(() => {
    if (refresh !== lastRefreshRef.current && refresh === true) {
      invalidateArchive();
      refetchArchive();
    }
    lastRefreshRef.current = refresh;
  }, [refresh]);

  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.setGridOption("quickFilterText", searchText);
    }
  }, [searchText]);

  const unarchiveProject = (selectedProjects) => {
    Project.unarchive(selectedProjects.map((s) => s.id)).finally(() => {
      setSelectedProjects([]);
      invalidateArchive();
      setRefresh((r) => !r);
    });
  };

  const deleteProject = (selectedProjects) => {
    Project.delete(selectedProjects.map((s) => s.id)).finally(() => {
      setSelectedProjects([]);
      invalidateArchive();
      setRefresh((r) => !r);
    });
  };

  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedProjects(selected);
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setMenuPosition({ x: e.pageX, y: e.pageY });
    setMenuVisible(true);
  };

  const handleCloseMenu = () => {
    setMenuVisible(false);
  };

  const colDefs = [
    { field: "id", hide: true },
    {
      headerName: "Name",
      field: "name",
      headerCheckboxSelection: true,
      cellStyle: { fontWeight: 500, ...leftAlignColDef.cellStyle },
      ...defaultColDef,
    },
    {
      headerName: "Location",
      field: "location",
      ...defaultColDef,
      ...leftAlignColDef,
    },
    {
      headerName: "Year",
      field: "year",
      ...defaultColDef,
      ...leftAlignColDef,
    },
    {
      headerName: "GWP (kg CO₂e)",
      field: "gwp",
      ...rightAlignColDef,
      ...defaultColDef,
      ...floatFormater,
    },
  ];

  const contextMenuOptions = [{ label: "Delete", action: deleteProject, needSelectedItems: true }];

  return (
    <MainContainer>
      <PageTitleContainer>
        <Box>
          <Typography variant="Page Title">{t("Project Archive")}</Typography>
          <Typography variant="Page Subtitle">{t("View and manage your archived projects")}</Typography>
        </Box>
      </PageTitleContainer>

      <Paper variant="Table Container">
        <TableHeaderContainer>
          <TableTitleContainer>
            <Typography variant="Table Title">
              {t("Archived Projects")} ({projects.length})
            </Typography>
          </TableTitleContainer>
          <TableHeaderToolsContainer ref={gridHeaderContainerRef}>
            <SearchBox text={searchText} setText={setSearchText} />
            <ExportAsXLSXButton data={projects} label="Export as .xlsx" filename="archived_projects.xlsx" />
            <RefreshButton setRefresh={setRefresh} label="Refresh table" />
          </TableHeaderToolsContainer>
        </TableHeaderContainer>
        <Box>
          <ResizableBox>
            <AgGrid ref={gridRef} columnDefs={colDefs} rowData={projects} onSelectionChanged={onSelectionChanged} handleContextMenu={handleContextMenu} headerCheckbox={false} />
          </ResizableBox>
        </Box>
      </Paper>

      <Box sx={{ paddingY: 1 }} />

      <Box sx={{ display: selectedProjects.length === 1 ? "block" : "none" }}>
        <SystemBoundary processData={processData} selectedLanguage={selectedLanguage} />
        <Footprint processData={processData} selectedLanguage={selectedLanguage} pageType="utilizer" />
        <SankeyDiagram processData={processData} selectedLanguage={selectedLanguage} selectedRow={selectedProjects[0]} />
      </Box>

      <ContextMenu x={menuPosition.x} y={menuPosition.y} visible={menuVisible} onClose={handleCloseMenu} selectedItems={selectedProjects} options={contextMenuOptions} />
    </MainContainer>
  );
};

export default ProjectArchive;
